import { useContext, useEffect, useRef, useState } from "react";
import ScrollListContainer from "../../common/ScrollListContainer";
import {
  AbortContext,
  INonPatientGroupData,
  INonPatientGroupsData,
} from "@home-health-notify/domain";
import { List } from "@mui/material";
import AddGroupListItem from "./AddGroupListItem";
import {
  Constants,
  IOrganizationContext,
  OrganizationContext,
  useDebouncedAbortableCall,
  useScrollLock,
  useTranslation,
} from "hhn-commons";
import ProgressLoader from "../../loader/ProgressLoader";
import EmptyRecordView from "../../common/EmptyRecordView";

type GroupsListType = {
  searchText: string | undefined;
};

const GroupsList = ({ searchText }: GroupsListType) => {
  const { t } = useTranslation();
  const tNs = (key: string) => t(key, { ns: "advanceSearch" });

  const { selectedOrgUser }: IOrganizationContext =
    useContext(OrganizationContext);
  const chatManager = selectedOrgUser?.chatManager.getNonPatientChatManager();

  const [isLoading, setIsLoading] = useState<boolean>(false);
  const [chatGroups, setChatGroups] = useState<INonPatientGroupsData>({
    data: [],
    page: 1,
    limit: 0,
    total: 1,
  });
  const { concurrencySafe } = useScrollLock();
  const abortContextRef = useRef<AbortContext>(undefined);

  // Function to fetch chat groups with search and filters
  const fetchChatGroups = async (
    searchText?: string,
    isSearchClear?: boolean,
    context?: AbortContext,
  ) => {
    if (chatGroups.data.length < chatGroups.total && chatManager) {
      setIsLoading(true);
      await chatManager.fetchChatGroup(searchText, isSearchClear, context);
      setChatGroups(chatManager.getFetchedChats());
      if (context && !context.isAborted) {
        setIsLoading(false);
      }
    }
  };

  // Function to scroll and fetch chat groups
  const scrollChatGroups = async (
    searchText?: string,
    isSearchClear?: boolean,
    context?: AbortContext,
  ) => {
    await concurrencySafe(async () => {
      await fetchChatGroups(searchText, isSearchClear, context);
    });
  };

  const resetChatGroups = () => {
    setChatGroups({
      data: [],
      page: 1,
      limit: 0,
      total: 1,
    });
  };

  const handleSearch = useDebouncedAbortableCall(
    async (abortContext: AbortContext, searchText: string) => {
      abortContextRef.current?.abort();
      abortContextRef.current = abortContext;
      await fetchChatGroups(searchText, true, abortContext);
    },
    Constants.API_DEBOUNCE_DELAY_IN_MS,
  );

  useEffect(() => {
    if (searchText) {
      resetChatGroups();
      handleSearch(searchText);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [searchText]);

  useEffect(() => {
    (async () => {
      abortContextRef.current = new AbortContext();
      await scrollChatGroups(undefined, undefined, abortContextRef.current);
    })();

    return () => {
      abortContextRef.current?.abort();
      chatManager?.resetChatGroup();
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  return (
    <>
      <ScrollListContainer
        isLoading={isLoading}
        onScrollCallback={() => scrollChatGroups(searchText)}
        dataTestId=""
        resetTrigger={searchText}
      >
        <List data-testid="chatList" sx={{ paddingTop: "0px" }}>
          {chatGroups.data.map((group: INonPatientGroupData, index: number) => {
            return (
              <AddGroupListItem key={group.id + index} chatGroup={group} />
            );
          })}
          {!isLoading && chatGroups.data.length === 0 && (
            <EmptyRecordView emptyMessage={t("emptySearchRecordMessage")} />
          )}
          {isLoading && (
            <ProgressLoader
              label={tNs("loading")}
              style={{
                height: "auto",
              }}
            />
          )}
        </List>
      </ScrollListContainer>
    </>
  );
};

export default GroupsList;
